import type { PaymentSubState } from '@diamond/shared/types';
import {
  buyerCaptureException,
  firebaseVapidKey,
  getFirebaseApp,
  LIST_FEATURE_FLAGS,
  useFeatureFlag,
} from '@diamond/shared/utils';
import {
  buildOrderDetailPaymentProofRoute,
  resolvePaymentDestination,
} from '@diamond/sol-buyer/utils';
import {
  getOrderDetailOptions,
  NOTIFICATION_QUERY_KEY,
  putNotificationToken,
  putReadNotification,
  useAuthStore,
  useShipToStore,
} from '@diamond/sol-buyer-context';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

import { parseNotificationTarget } from '../notification/parseNotificationMetadata';
import {
  type WebPushGateInput,
  canOfferWebPushRecovery,
  canPromptWebPush,
  canRegisterWebPushToken,
  detectIosDevice,
  readInstalledDisplayMode,
} from './canPromptWebPush';
import {
  registerPushServiceWorker,
  unregisterPushServiceWorker,
} from './push-service-worker';
import {
  type WebPushPayload,
  getWebPushNotificationShowOptions,
} from './web-push-display';
import {
  type WebPushClickMessage,
  isWebPushClickMessage,
  isWebPushForegroundMessage,
} from './web-push-message';
import { WEB_PUSH_PERMISSION_CHECK_EVENT } from './web-push-permission-check';

export type SyncWebPushInput = {
  accessToken: string | null;
  isEnabled: boolean;
  isIos: boolean;
  isServiceWorkerReady: boolean;
  isStandalone: boolean;
  notificationPermission: NotificationPermission | null;
  shouldDeleteToken: boolean;
};

export type UseWebPushNotificationsInput = {
  isEnabled: boolean;
  isLoading: boolean;
  shouldDeleteToken: boolean;
};

export type WebPushPromptControls = {
  canPrompt: boolean;
  canShowRecovery: boolean;
  requestPermission: () => Promise<NotificationPermission | null>;
};

let lastSentFcmToken: string | null = null;
let hasReportedMissingVapidKey = false;

export function resetWebPushRuntime() {
  lastSentFcmToken = null;
  hasReportedMissingVapidKey = false;
}

function toGateInput(input: SyncWebPushInput): WebPushGateInput {
  return {
    isAuthenticated: Boolean(input.accessToken),
    isIos: input.isIos,
    isServiceWorkerReady: input.isServiceWorkerReady,
    isStandalone: input.isStandalone,
    isWebPushEnabled: input.isEnabled,
    notificationPermission: input.notificationPermission,
  };
}

type FirebaseMessagingApi = typeof import('firebase/messaging');

async function loadFirebaseMessaging(): Promise<FirebaseMessagingApi> {
  return import('firebase/messaging');
}

async function getMessagingIfSupported() {
  const messagingApi = await loadFirebaseMessaging();
  if (!(await messagingApi.isSupported())) return undefined;
  const app = getFirebaseApp();
  if (!app) return undefined;
  return {
    api: messagingApi,
    instance: messagingApi.getMessaging(app),
  };
}

function captureWebPushFailure(message: string) {
  buyerCaptureException({ message }, (scope) => {
    scope.setTag('feature', 'web-push');
  });
}

async function registerWebPushToken(
  accessToken: string,
  messaging: Awaited<ReturnType<typeof getMessagingIfSupported>>
) {
  if (!messaging || !('serviceWorker' in navigator)) return;
  if (!firebaseVapidKey) {
    reportMissingVapidKey();
    return;
  }

  const registration = await registerPushServiceWorker();
  let fcmToken: string;
  try {
    fcmToken = await messaging.api.getToken(messaging.instance, {
      serviceWorkerRegistration: registration,
      vapidKey: firebaseVapidKey,
    });
  } catch {
    captureWebPushFailure('Failed to get web push token');
    return;
  }

  if (!fcmToken || fcmToken === lastSentFcmToken) return;

  try {
    await putNotificationToken(accessToken, fcmToken);
    lastSentFcmToken = fcmToken;
  } catch {
    captureWebPushFailure('Failed to save web push token');
  }
}

function reportMissingVapidKey() {
  if (hasReportedMissingVapidKey) return;
  hasReportedMissingVapidKey = true;
  captureWebPushFailure(
    'Missing NX_PUBLIC_FIREBASE_VAPID_KEY in buyer frontend build'
  );
}

export async function syncWebPushSubscription(input: SyncWebPushInput) {
  if (
    !input.isEnabled &&
    !input.shouldDeleteToken &&
    lastSentFcmToken === null
  ) {
    return;
  }

  const messaging = await getMessagingIfSupported();

  if (!input.isEnabled) {
    lastSentFcmToken = null;
    if (messaging) {
      try {
        await messaging.api.deleteToken(messaging.instance);
      } catch {
        captureWebPushFailure('Failed to delete web push token');
      }
    }
    await unregisterPushServiceWorker();
    return;
  }

  if (!messaging || !input.accessToken) return;

  if (!canRegisterWebPushToken(toGateInput(input))) return;

  await registerWebPushToken(input.accessToken, messaging);
}

export async function handleForegroundPushMessage(
  queryClient: QueryClient,
  payload?: WebPushPayload
) {
  void queryClient.invalidateQueries({
    queryKey: [NOTIFICATION_QUERY_KEY],
  });
  if (!payload || window.Notification.permission !== 'granted') return;

  const show = getWebPushNotificationShowOptions(payload);
  if (!show.title && !show.options.body) return;

  try {
    const registration = await navigator.serviceWorker.getRegistration(
      '/push/'
    );
    if (!registration) return;
    await registration.showNotification(
      show.title || 'Sukanda OneLink',
      show.options
    );
  } catch {
    captureWebPushFailure('Failed to display foreground web push notification');
  }
}

export async function handleWebPushWorkerMessage(
  data: unknown,
  options: {
    accessToken: string | null;
    fetchWaitingPaymentOrder: (
      orderMetaId: string,
      warehouse: string
    ) => Promise<{ payment_sub_state: PaymentSubState }>;
    isPaymentInstructionEnabled: boolean;
    navigate: (href: string) => unknown;
    warehouseId: string;
  }
) {
  if (!isWebPushClickMessage(data)) return;

  const message: WebPushClickMessage = data;
  if (message.notification_id && options.accessToken && options.warehouseId) {
    try {
      await putReadNotification(options.accessToken, message.notification_id, {
        warehouse_id: options.warehouseId,
      });
    } catch {
      captureWebPushFailure('Failed to mark web push notification as read');
    }
  }

  const href = await resolveWebPushClickHref(message, {
    accessToken: options.accessToken,
    fetchWaitingPaymentOrder: options.fetchWaitingPaymentOrder,
    isPaymentInstructionEnabled: options.isPaymentInstructionEnabled,
  });
  if (href) {
    await options.navigate(href);
  }
}

async function resolveWebPushClickHref(
  message: WebPushClickMessage,
  options: {
    accessToken: string | null;
    fetchWaitingPaymentOrder: (
      orderMetaId: string,
      warehouse: string
    ) => Promise<{ payment_sub_state: PaymentSubState }>;
    isPaymentInstructionEnabled: boolean;
  }
): Promise<string> {
  const target = parseNotificationTarget(message.data ?? {});
  if (target.kind !== 'order' || !target.isWaitingForPayment) {
    return message.href;
  }

  const proofHref = buildOrderDetailPaymentProofRoute({
    orderMetaId: target.orderMetaId,
    warehouse: target.warehouse,
  });

  if (!options.accessToken || !options.isPaymentInstructionEnabled) {
    return proofHref;
  }

  try {
    const order = await options.fetchWaitingPaymentOrder(
      target.orderMetaId,
      target.warehouse
    );
    return resolvePaymentDestination({
      isPaymentInstructionEnabled: true,
      orderMetaId: target.orderMetaId,
      paymentSubState: order.payment_sub_state,
      warehouse: target.warehouse,
    }).href;
  } catch {
    return proofHref;
  }
}

function readWebPushEnvironment(): Pick<
  SyncWebPushInput,
  'isIos' | 'isStandalone' | 'notificationPermission'
> {
  const notificationPermission =
    typeof window === 'undefined' || typeof window.Notification === 'undefined'
      ? null
      : window.Notification.permission;

  return {
    isIos: detectIosDevice({
      maxTouchPoints: navigator.maxTouchPoints,
      platform: navigator.platform,
      userAgent: navigator.userAgent,
    }),
    isStandalone: readInstalledDisplayMode(),
    notificationPermission,
  };
}

export function useWebPushNotifications({
  isEnabled,
  isLoading,
  shouldDeleteToken,
}: UseWebPushNotificationsInput): WebPushPromptControls {
  const accessToken = useAuthStore((state) => state.access_token);
  const warehouseId = useShipToStore((state) => state.selectedShipToId);
  const isPaymentInstructionEnabled = useFeatureFlag(
    LIST_FEATURE_FLAGS.FF_PAYMENT_INSTRUCTION_PAGE,
    false
  );
  const queryClient = useQueryClient();
  const router = useRouter();
  const [webPushEnvironment, setWebPushEnvironment] = useState<
    Pick<
      SyncWebPushInput,
      | 'isIos'
      | 'isServiceWorkerReady'
      | 'isStandalone'
      | 'notificationPermission'
    >
  >({
    isIos: false,
    isServiceWorkerReady: false,
    isStandalone: false,
    notificationPermission: null,
  });

  useEffect(() => {
    if (isLoading) return;

    const abort = new AbortController();

    void (async () => {
      const environment = readWebPushEnvironment();
      if (!isEnabled) {
        setWebPushEnvironment({
          isServiceWorkerReady: false,
          ...environment,
        });
        return;
      }

      if (!firebaseVapidKey) {
        reportMissingVapidKey();
        setWebPushEnvironment({
          isServiceWorkerReady: false,
          ...environment,
        });
        return;
      }

      let isServiceWorkerReady = false;
      if ('serviceWorker' in navigator) {
        try {
          await registerPushServiceWorker();
          isServiceWorkerReady = true;
        } catch {
          captureWebPushFailure('Failed to register push service worker');
        }
      }
      if (abort.signal.aborted) return;

      setWebPushEnvironment({
        isServiceWorkerReady,
        ...environment,
      });
    })();

    return () => abort.abort();
  }, [isEnabled, isLoading]);

  useEffect(() => {
    if (isLoading) return;

    void syncWebPushSubscription({
      accessToken,
      isEnabled,
      shouldDeleteToken,
      ...webPushEnvironment,
    });
  }, [
    accessToken,
    isEnabled,
    isLoading,
    shouldDeleteToken,
    webPushEnvironment,
  ]);

  const requestPermission = useCallback(async () => {
    if (typeof window.Notification === 'undefined') return null;

    try {
      const permission = await window.Notification.requestPermission();
      setWebPushEnvironment((current) => ({
        ...current,
        notificationPermission: permission,
      }));
      return permission;
    } catch {
      captureWebPushFailure('Failed to request web push permission');
      return window.Notification.permission;
    }
  }, []);

  useEffect(() => {
    if (isLoading) return;

    const refreshPermission = () => {
      const notificationPermission =
        typeof window.Notification === 'undefined'
          ? null
          : window.Notification.permission;
      setWebPushEnvironment((current) =>
        current.notificationPermission === notificationPermission
          ? current
          : { ...current, notificationPermission }
      );
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refreshPermission();
    };

    window.addEventListener('focus', refreshPermission);
    window.addEventListener(WEB_PUSH_PERMISSION_CHECK_EVENT, refreshPermission);
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return () => {
      window.removeEventListener('focus', refreshPermission);
      window.removeEventListener(
        WEB_PUSH_PERMISSION_CHECK_EVENT,
        refreshPermission
      );
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [isLoading]);

  useEffect(() => {
    if (isLoading || !('serviceWorker' in navigator)) return;

    let unsubscribe = () => undefined;
    let cancelled = false;

    if (isEnabled) {
      void (async () => {
        const messaging = await getMessagingIfSupported();
        if (!messaging || cancelled) return;
        unsubscribe = messaging.api.onMessage(messaging.instance, (payload) => {
          void handleForegroundPushMessage(queryClient, payload);
        });
      })();
    }

    const onWorkerMessage = (event: MessageEvent) => {
      if (isWebPushForegroundMessage(event.data)) {
        handleForegroundPushMessage(queryClient);
        return;
      }
      void handleWebPushWorkerMessage(event.data, {
        accessToken,
        fetchWaitingPaymentOrder: async (orderMetaId, warehouse) => {
          if (!accessToken) {
            throw new Error('missing access token');
          }
          const order = await queryClient.fetchQuery(
            getOrderDetailOptions(accessToken, orderMetaId, warehouse)
          );
          return { payment_sub_state: order.payment_sub_state };
        },
        isPaymentInstructionEnabled,
        navigate: (href) => router.push(href),
        warehouseId,
      });
    };

    navigator.serviceWorker.addEventListener('message', onWorkerMessage);

    return () => {
      cancelled = true;
      unsubscribe();
      navigator.serviceWorker.removeEventListener('message', onWorkerMessage);
    };
  }, [
    accessToken,
    isEnabled,
    isLoading,
    isPaymentInstructionEnabled,
    queryClient,
    router,
    warehouseId,
  ]);

  return {
    canPrompt: canPromptWebPush(
      toGateInput({
        accessToken,
        isEnabled,
        shouldDeleteToken,
        ...webPushEnvironment,
      })
    ),
    canShowRecovery: canOfferWebPushRecovery(
      toGateInput({
        accessToken,
        isEnabled,
        shouldDeleteToken,
        ...webPushEnvironment,
      })
    ),
    requestPermission,
  };
}
